/**
 * RecoveryKeyModal Component
 * Modal that prompts for recovery key after login when required
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import logger from '../../utils/logger';
import PropTypes from 'prop-types';
import RecoveryKeyInput, { normalizeRecoveryKey } from './RecoveryKeyInput';
// Note: Modal confirm/warn removed for immediate actions in tests
import { useRecovery, useRecoveryActions } from '../../context/RecoveryContext/index';
import './RecoveryKeyModal.css';

// Error code mappings
const ERROR_MESSAGES = {
  INVALID_FORMAT: 'Please enter a valid recovery key (48 characters in base58 format)',
  INVALID_RECOVERY_KEY: 'The recovery key is incorrect. Please check and try again.',
  NETWORK_TIMEOUT: 'Network connection timed out. Please try again.',
  CORRUPTED_BACKUP: 'The backup data appears to be corrupted. Please contact support.',
  CRYPTO_NOT_READY: 'Encryption engine is starting up. Please wait a moment and try again.',
  VERIFY_FAILED: 'Couldn’t verify the key due to an internal error. Please try again.',
  NO_BACKUP: 'No key backup was found for this account.',
  SSSS_VERSION_MISMATCH: 'Recovery key version mismatch. Please use the latest key.',
  VERIFICATION_CONFLICT: 'Device verification in progress. Please complete verification first.',
  TOO_MANY_ATTEMPTS: 'Too many attempts. Please wait before trying again.',
  PERSISTENCE_ERROR: 'Failed to save recovery data. Please try again.',
  UNKNOWN: 'An unexpected error occurred. Please try again.',
};

const RecoveryKeyModal = ({
  onSuccess,
  onSkip,
  onReset,
  validator,
  allowSkip = true,
  allowReset = true,
}) => {
  const [keyValue, setKeyValue] = useState('');
  const [formatValid, setFormatValid] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  // Removed confirm/warn UI for skip/reset to make actions immediate
  const [remainingAttempts, setRemainingAttempts] = useState(null);
  const [cooldownSeconds, setCooldownSeconds] = useState(0);

  const inputRef = useRef(null);
  const cooldownTimerRef = useRef(null);

  // const { showWarning, showConfirm } = useModal();
  const recoveryState = useRecovery();
  const recoveryActions = useRecoveryActions();

  // Focus management
  useEffect(() => {
    // Focus input on mount
    if (inputRef.current) {
      inputRef.current.focus();
    }

    return () => {
      // Clear cooldown timer on unmount
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, []);

  // Handle cooldown countdown
  useEffect(() => {
    if (cooldownSeconds > 0) {
      cooldownTimerRef.current = setInterval(() => {
        setCooldownSeconds((prev) => {
          if (prev <= 1) {
            clearInterval(cooldownTimerRef.current);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (cooldownTimerRef.current) {
        clearInterval(cooldownTimerRef.current);
      }
    };
  }, [cooldownSeconds]);

  /**
   * Handle key input change
   */
  const handleKeyChange = useCallback((value, meta) => {
    setKeyValue(value);
    setFormatValid(meta.isFormatValid);
    setError(null); // Clear error on new input
  }, []);

  /**
   * Handle form submission
   */
  const handleSubmit = useCallback(async (e) => {
    e?.preventDefault();

    if (!formatValid || loading || cooldownSeconds > 0) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Normalize the key
      const { normalized } = normalizeRecoveryKey(keyValue);

      // Validate and submit through the validator
      const result = await validator.onSubmit(normalized);

      if (result.success) {
        // Clear sensitive data
        setKeyValue('');

        // Call success callback
        if (onSuccess) {
          await onSuccess(result);
        }

        // Update recovery context
        if (recoveryActions?.submitKey) {
          await recoveryActions.submitKey(normalized);
        }
      } else {
        // Handle validation error
        const errorCode = result.error || 'UNKNOWN';
        const errorMessage = ERROR_MESSAGES[errorCode] || ERROR_MESSAGES.UNKNOWN;

        setError(errorMessage);

        // Handle rate limiting
        if (errorCode === 'TOO_MANY_ATTEMPTS') {
          const seconds = result.cooldownSeconds ?? (result.cooldownMs ? Math.ceil(result.cooldownMs / 1000) : 0);
          if (seconds > 0) setCooldownSeconds(seconds);
        }

        // Update remaining attempts if provided
        if (result.remainingAttempts !== undefined) {
          setRemainingAttempts(result.remainingAttempts);
        }

        // Clear sensitive data on certain errors
        if (['CORRUPTED_BACKUP', 'SSSS_VERSION_MISMATCH'].includes(errorCode)) {
          setKeyValue('');
          setFormatValid(false);
        }
      }
    } catch (err) {
      logger.error('RecoveryKeyModal: Submission error', err);
      setError(ERROR_MESSAGES.UNKNOWN);
    } finally {
      setLoading(false);
    }
  }, [keyValue, formatValid, loading, cooldownSeconds, validator, onSuccess, recoveryActions]);

  /**
   * Handle Skip action
   */
  const handleSkip = useCallback(() => {
    if (!allowSkip) return;

    // Trigger skip immediately without confirm modal
    if (onSkip) {
      onSkip();
    }
    if (recoveryActions?.skip) {
      recoveryActions.skip();
    }
  }, [allowSkip, onSkip, recoveryActions]);

  /**
   * Handle Reset backup action
   */
  const handleReset = useCallback(async () => {
    if (!allowReset) return;

    // Trigger reset immediately without confirm modal
    setLoading(true);
    try {
      if (onReset) {
        await onReset();
      }
      if (recoveryActions?.reset) {
        await recoveryActions.reset();
      }
    } catch (err) {
      logger.error('RecoveryKeyModal: Reset backup error', err);
      setError('Failed to reset backup. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [allowReset, onReset, recoveryActions]);

  /**
   * Handle keyboard shortcuts
   */
  const handleKeyDown = useCallback((e) => {
    // Enter key submits if valid
    if (e.key === 'Enter' && formatValid && !loading && cooldownSeconds === 0) {
      handleSubmit(e);
    }

    // Escape key opens skip dialog if allowed
    if (e.key === 'Escape' && allowSkip && !loading) {
      e.preventDefault();
      handleSkip();
    }
  }, [formatValid, loading, cooldownSeconds, allowSkip, handleSubmit, handleSkip]);

  const isSubmitDisabled = !formatValid || loading || cooldownSeconds > 0;

  return (
    <div
      className="recovery-key-modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="recovery-modal-title"
      aria-describedby="recovery-modal-description"
      data-testid="recovery-key-modal"
    >
      <div className="recovery-key-modal-container">
        <div className="recovery-key-modal-header">
          <h2 id="recovery-modal-title" className="recovery-key-modal-title">
            Enter Your Recovery Key
          </h2>
        </div>

        <div className="recovery-key-modal-content">
          <div id="recovery-modal-description" className="recovery-key-modal-description">
            <p>
              Your recovery key is needed to access your encrypted message history.
              It consists of 48 characters using digits 1-9 and letters (excluding 0, O, I, l).
            </p>
            <div className="recovery-key-example">
              <span className="recovery-key-example-label">Example format:</span>
              <code className="recovery-key-example-code">
                2A3b 4C5d 6E7f 8G9h ... (12 groups of 4 characters)
              </code>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="recovery-key-form">
            <RecoveryKeyInput
              value={keyValue}
              onChange={handleKeyChange}
              disabled={loading}
              autoFocus
              inputRef={inputRef}
              onKeyDown={handleKeyDown}
              error={!!error}
              aria-describedby={error ? 'recovery-error-message' : 'recovery-modal-description'}
            />

            {error && (
              <div
                id="recovery-error-message"
                className="recovery-key-error"
                role="alert"
                aria-live="polite"
                data-testid="error-message"
              >
                <ion-icon name="alert-circle" />
                <span>{error}</span>
              </div>
            )}

            {remainingAttempts !== null && remainingAttempts < 3 && (
              <div className="recovery-key-warning" role="status">
                {remainingAttempts > 0
                  ? `${remainingAttempts} attempt${remainingAttempts === 1 ? '' : 's'} remaining`
                  : 'No attempts remaining'}
              </div>
            )}

            {cooldownSeconds > 0 && (
              <div className="recovery-key-cooldown" role="status">
                Please wait {cooldownSeconds} seconds before trying again
              </div>
            )}

            <div className="recovery-key-modal-actions">
              <button
                type="submit"
                className="recovery-key-btn recovery-key-btn-primary"
                disabled={isSubmitDisabled}
                data-testid="continue-btn"
                aria-busy={loading}
              >
                {loading ? (
                  <>
                    <span
                      className="recovery-key-spinner"
                      data-testid="loading-spinner"
                      aria-hidden="true"
                    />
                    Verifying...
                  </>
                ) : cooldownSeconds > 0 ? (
                  `Wait ${cooldownSeconds}s`
                ) : (
                  'Continue'
                )}
              </button>

              {allowSkip && (
                <button
                  type="button"
                  className="recovery-key-btn recovery-key-btn-secondary"
                  onClick={handleSkip}
                  disabled={loading}
                  data-testid="skip-btn"
                >
                  Skip for Now
                </button>
              )}

              {allowReset && (
                <button
                  type="button"
                  className="recovery-key-btn recovery-key-btn-danger"
                  onClick={handleReset}
                  disabled={loading}
                  data-testid="reset-backup-btn"
                >
                  Reset Backup
                </button>
              )}
            </div>

            <div className="recovery-key-modal-footer">
              <a
                href="https://matrix.org/docs/guides/e2e_implementation#recovery-key"
                target="_blank"
                rel="noopener noreferrer"
                className="recovery-key-help-link"
                data-testid="help-link"
              >
                <ion-icon name="help-circle-outline" />
                Learn more about recovery keys
              </a>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

RecoveryKeyModal.propTypes = {
  onSuccess: PropTypes.func,
  onSkip: PropTypes.func,
  onReset: PropTypes.func,
  validator: PropTypes.shape({
    onSubmit: PropTypes.func.isRequired,
  }).isRequired,
  allowSkip: PropTypes.bool,
  allowReset: PropTypes.bool,
};

export default RecoveryKeyModal;
